import moment from 'moment'
import {
    Device,
    Gateway,
    Logger
} from '../../api'

const initialState = () => {
    return {
        nodesArray: [],
    }
}

export const nodes = {
    namespaced: true,
    state: initialState(),
    actions: {
        async fetchNodes({ commit }) {
            try {
                const DEFAULT_ZONE = 'Z00'
                const nodesAPI = await Device.get('Neurolink')
                const nodesArray = []
                nodesAPI.data.forEach((node) => {
                    const itHasZoneAssigned = node.functions.hasOwnProperty('ZoneNode')
                    nodesArray.push({
                        id: node.functions.Basic.assetId,
                        zone_id: itHasZoneAssigned 
                                    ? node.functions.ZoneNode.zoneID 
                                    : DEFAULT_ZONE,
                    })
                })
                commit('SET_NODES', nodesArray)
            } catch (error) {
                commit('SET_NODES', [])
                throw { type: 'node', error }
            }
        },
        async addNode({ commit }, data) {
            try {
                const { node } = data
                const params = {
                    function: 'Basic',
                    class: 'Neurolink',
                    displayName: node,
                }
                const mongoValidation = await Device.post(node, params)
                if (!mongoValidation) return false
                if (mongoValidation.data.status !== 200) return false
                commit('ADD_NODE', data)
                return true
            } catch {
                return false
            }
        },
        async fetchMongoValidation({ state }, mongoStatus) {
            let mongoValidation
            const wait = (ms) =>
                new Promise((resolve) => setTimeout(resolve, ms))
            for (let index = 1; index <= 16; index++) {
                await wait(1000)
                mongoValidation = await Logger.getStatus(mongoStatus.data)
                if (!mongoValidation.data) continue
                if (mongoValidation.data) {
                    if (mongoValidation.data.status === 'RECEIVED') break
                }
            }
            return mongoValidation
        },
        async controlNodes({ dispatch, rootState }, data) {
            try {
                const { nodes, config, gateway } = data
                const user_id = rootState.user.data._id
                const mongoStatus = await Gateway.sendInstructions(
                    nodes,
                    'devices',
                    config,
                    gateway,
                    user_id
                )
                const mongoValidation = await dispatch(
                    'fetchMongoValidation',
                    mongoStatus
                )
                if (mongoValidation.data === null) return 'ERROR'
                if (mongoValidation.data.status !== 'RECEIVED')
                    return mongoValidation.data.status
                return mongoValidation.data.status
            } catch {
                return 'ERROR'
            }
        },
        async deleteNode({ commit }, data) {
            let deletedItem = true
            try {
                const { node } = data
                const mongoValidation = await Device.deletion(node)
                if (!mongoValidation.data)
                    throw new Error(
                        'Internal error in database when deleting selected node, please contact your provider.'
                    )
                commit('DELETE_NODE', node)
                return { deletedItem }
            } catch (error) {
                deletedItem = false
                return { error, deletedItem }
            }
        },
        async fetchLogs({ rootState }, logsData) {
            try {
                const ZONES_TYPE = 'zones'
                let logsFromApi
                const logMessages = []
                const date = new Date()
                const offset = moment.parseZone(date).utcOffset()

                const { data } = logsData

                if (data) {
                    const { mode, value } = data
                    if (mode === 'targets') {
                        logsFromApi = await Logger.getTarget(value)
                    } else if (mode === 'schedule') {
                        const gateway = rootState.gateways.mainGateway
                        logsFromApi = await Logger.getSchedules(value, gateway)
                    } else {
                        logsFromApi = await Logger.getQty(value)
                    }
                } else {
                    logsFromApi = await Logger.get()
                }

                if (logsFromApi?.data?.logs) {
                    logsFromApi.data.logs.forEach((log) => {
                        let messageForLog
                        const date = new Date(log.closed)
                        const timeInMilis = date.getTime()
                        const logStatus = log.status
                        const functionInLog = log.data.messages[0].function
                        const targetsArray = [...log.data.targets]

                        let mainTargetType =
                            log.targetsType === ZONES_TYPE ? logsData.t('control_center.zones.zone') : logsData.t('control_center.devices.device')
                        mainTargetType =
                            log.data.targets.length > 1
                                ? `${mainTargetType}s`
                                : mainTargetType

                        const targetsFormatted =
                            log.targetsType === ZONES_TYPE
                                ? targetsArray.map((target) => {
                                      const zoneTargeted =
                                          rootState.zones.zonesArray.find(
                                              (zone) => zone.id === target
                                          )
                                      return zoneTargeted?.name ?? target
                                  })
                                : targetsArray

                        const targetsChain = targetsFormatted.join(', ')
                        const targetsForMessage = `${mainTargetType} ${targetsChain}`

                        if (functionInLog === 'MotionSensor') {
                            const minimumLevel =
                                log.data.messages[1]?.data?.minLevelConfiguration
                            const maximumLevel =
                                log.data.messages[1]?.data?.maxLevelConfiguration
                            messageForLog = [
                                logsData.t('logs.motion_sensor_mode', { targets: targetsForMessage }),
                                logsData.t('logs.levels', { min: minimumLevel, max: maximumLevel })
                            ]
                        } else if (functionInLog === 'SmartActuator') {
                            const illuminationLevel =
                                log.data.messages[0].data.targetLightCommand
                            messageForLog = [
                                logsData.t('logs.smart_actuator_mode', { targets: targetsForMessage }),
                                logsData.t('logs.level', { level: illuminationLevel })
                            ]
                        } else if (functionInLog === 'CommandSender') {
                            if (
                                log.data.messages[0].hasOwnProperty('commands')
                            ) {
                                log.data.messages[0].data = { commands: [] }
                                log.data.messages[0].data.commands =
                                    log.data.messages[0].commands
                                delete log.data.messages[0]['commands']
                            }

                            const operationMode = logsData.t("logs.command_sender_mode", {
                                name:
                                  log.data.messages[0].data.commands.length > 1
                                    ? logsData.t('commands.auto').toLowerCase()
                                    : logsData.t('commands.manual').toLowerCase()
                            });

                            const messageInfo =
                                log.data.messages[0].data.commands.length > 1
                                    ? {
                                          minimumLevel:
                                              log.data.messages[0].data
                                                  .commands[1].data
                                                  .minLevelConfiguration,
                                          maximumLevel:
                                              log.data.messages[0].data
                                                  .commands[1].data
                                                  .maxLevelConfiguration,
                                      }
                                    : {
                                          illuminationLevel:
                                              log.data.messages[0].data
                                                  .commands[0].data
                                                  .targetLightCommand,
                                      }

                            const messageConfiguration =
                                log.data.messages[0].data.commands.length > 1
                                    ? logsData.t('logs.levels', { min: messageInfo.minimumLevel, max: messageInfo.maximumLevel })
                                    : logsData.t('logs.level', { level: messageInfo.illuminationLevel })

                            messageForLog = [
                                logsData.t('logs.command_sender_message', { targets: targetsForMessage, mode: operationMode }),
                                `${messageConfiguration}`,
                            ]
                        } else if (functionInLog === 'ZoneNode') {
                            const newZone = rootState.zones.zonesArray.find(
                                (zone) =>
                                    zone.id === log.data.messages[0].data.zoneID
                            )
                            messageForLog = [
                                logsData.t('logs.zone_moved', { targets: targetsForMessage, name: newZone ? newZone.name : log.data.messages[0].data.zoneID })
                            ]
                        } else if (functionInLog === 'ScheduleConfigurator') {
                            const scheduleID = log.data.messages[0].data.id
                            const scheduleSelected = rootState.project?.attributes?.schedulesNames?.find(
                                (schedule) =>
                                    schedule.id === scheduleID
                            )

                            const scheduleName = scheduleSelected?.name || scheduleID

                            if (log.data.messages[0].delete === true) {
                                messageForLog = [
                                    logsData.t('logs.schedule_deleted', { name: scheduleName })
                                ]
                            } else if (log.data.messages[0].active === true) {
                                const objectSize = Object.keys(
                                    log.data.messages[0].data
                                ).length

                                if (objectSize > 1) {
                                    let { days, time } =
                                        log.data.messages[0].data
                                    const daysKeys = {
                                        Mon: 'monday_short',
                                        Tue: 'tuesday_short',
                                        Wed: 'wednesday_short',
                                        Thu: 'thursday_short',
                                        Fri: 'friday_short',
                                        Sat: 'saturday_short',
                                        Sun: 'sunday_short'
                                    };
                                    days && (days = days.split(',').map(day => logsData.t(`time.days_names.${daysKeys[day]}`)).join(','))
                                    const [hour, minutes] = time.split(':')
                                    let hourFromLog = Number(hour) + offset / 60
                                    if (hourFromLog < 0) hourFromLog += 24
                                    const formattedHour =
                                        hourFromLog < 10
                                            ? `0${hourFromLog}:${minutes}`
                                            : `${hourFromLog}:${minutes}`
                                    const timeInfo = logsData.t('logs.time_info', { days: days, time: formattedHour })

                                    const parametersInfo =
                                        log.data.messages[0].data.commands
                                            .length > 1
                                            ? {
                                                  minimumLevel:
                                                      log.data.messages[0].data
                                                          .commands[0].data
                                                          .minLevelConfiguration,
                                                  maximumLevel:
                                                      log.data.messages[0].data
                                                          .commands[0].data
                                                          .maxLevelConfiguration,
                                              }
                                            : {
                                                  illuminationLevel:
                                                      log.data.messages[0]?.data
                                                          ?.commands[0]?.data
                                                          ?.targetLightCommand,
                                              }

                                    const controlMode = logsData.t("logs.command_sender_mode", {
                                        name:
                                        log.data.messages[0].data.commands.length > 1
                                            ? logsData.t('commands.auto').toLowerCase()
                                            : logsData.t('commands.manual').toLowerCase()
                                    });
                                    
                                    const configurationMessage =
                                        log.data.messages[0].data.commands
                                            .length > 1
                                            ? logsData.t('logs.levels', { min: parametersInfo?.minimumLevel, max: parametersInfo.maximumLevel })
                                            : logsData.t('logs.level', { level: parametersInfo?.illuminationLevel })

                                    messageForLog = [
                                        logsData.t('logs.schedule_configured', { name: scheduleName, mode: controlMode }),
                                        logsData.t('logs.schedule_details', { message: configurationMessage, time: timeInfo }),
                                        `${targetsForMessage}`,
                                    ]
                                } else {
                                    messageForLog = [
                                        logsData.t('logs.schedule_activated', { name: scheduleName })
                                    ]
                                }
                            } else if (log.data.messages[0].active === false) {
                                messageForLog = [
                                    logsData.t('logs.schedule_desactivated', { name: scheduleName })
                                ]
                            }
                        } else {
                            messageForLog = [logsData.t('logs.unknown_command')]
                        }

                        const user = log.user

                        logMessages.push({
                            date: log.created,
                            type: log.targetsType ?? 'schedule',
                            time_ms: timeInMilis,
                            message: messageForLog,
                            targets: log.data.targets,
                            status: logStatus,
                            user: user,
                        })
                    })
                } else {
                    logsFromApi.data.forEach((log) => {
                        const date = new Date(log.timestamp)
                        const verbForText =
                            log.data.targets.length > 1 ? logsData.t('logs.plural_verb') : logsData.t('logs.singular_verb')

                        let mainTargetType =
                            log.data.targetsType === ZONES_TYPE
                                ? logsData.t('control_center.zones.zone')
                                : logsData.t('control_center.devices.device')
                        mainTargetType =
                            log.data.targets.length > 1
                                ? `${mainTargetType}s`
                                : mainTargetType

                        const targetsToFormat = [...log.data.targets]
                        const targetsFormatted =
                            log.data.targetsType === ZONES_TYPE
                                ? targetsToFormat.map((target) => {
                                      const targetZone =
                                          rootState.zones.zonesArray.find(
                                              (zone) => zone.id === target
                                          )
                                      return targetZone.name
                                  })
                                : targetsToFormat

                        const targetsChain = targetsFormatted.join(', ')

                        const mode = log.data.commands.find((m) =>
                            m.data.hasOwnProperty('controlModeType')
                        )

                        if (!mode) return

                        const targetsText = `${mainTargetType} ${targetsChain} ${verbForText}`
                        const fullText =
                            mode.data.controlModeType === 0
                                ? mode.data.targetLightCommand === 0
                                    ? [logsData.t('logs.turned_off', { target: targetsText })]
                                    : [
                                        logsData.t('logs.turned_on', { target: targetsText, level: mode.data.targetLightCommand })
                                      ]
                                : [
                                    logsData.t('logs.configured_automatically', { target: targetsText, min: mode.data.minLevelConfiguration, max: mode.data.maxLevelConfiguration })
                                  ]

                        logMessages.push({
                            date: log.timestamp,
                            time_ms: date.getTime(),
                            message: fullText,
                            targets: log.data.targets,
                            status: 'RECEIVED',
                        })
                    })
                }
                return logMessages
            } catch (error) {
                throw error
            }
        },
    },
    mutations: {
        ADD_NODE(state, data) {
            state.nodesArray.push({ id: data.node, zone_id: data.zone })
        },
        DELETE_NODE(state, nodeToDelete) {
            state.nodesArray = state.nodesArray.filter(node => node.id !== nodeToDelete)
        },
        SET_NODES(state, nodes) {
            state.nodesArray = nodes
        },
        RESET(state) {
            Object.assign(state, initialState())
        }
    },
}
